import { useMemo, useState } from 'react'
import { Badge, Grid, Button, Box, TextField, useTheme, useMediaQuery } from '@mui/material'
import { CropSpeciesField } from './crop-species-field'
import { CropVarietyField } from './crop-variety-field'
import { CropCountField } from './crop-count-field'
import { CropFilterPopper } from './crop-filter-popper'
import { CropCustomSpeciesRow } from './crop-custom-species-row'
import { Tune } from '@mui/icons-material'
import { DESKTOP_BREAKPOINT } from '~/common/constants/layout-constants'
import { useCropModalContext } from '../context/crop-modal-context'

export const CropFieldsRow = ({ cropArchetypes, sortedCropTemplates, filterSpeciesOptions, filterFamilyOptions, cropFamilyTypes, collectionTypes, handlers, crops, classifyTemplateForCrop, frostDates, farmClimate }) => {
  const { handleSpeciesChange, handleVarietyChange, handleCountChange } = handlers
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up(DESKTOP_BREAKPOINT))

  const { isCustomCrop, customSpeciesName, speciesTouched } = useCropModalContext()

  const [anchorEl, setAnchorEl] = useState(null)
  const [filterCropId, setFilterCropId] = useState(null)
  const [activeFilters, setActiveFilters] = useState([])

  const isFiltering = filterCropId !== null && activeFilters.length > 0
  const filterIconWidth = isDesktop ? '56px' : '40px'

  const filteredTemplates = useMemo(() => {
    if (!isFiltering) return sortedCropTemplates
    return (sortedCropTemplates || []).filter(template => {
      const rel = classifyTemplateForCrop(filterCropId, template)
      return activeFilters.includes(rel)
    })
  }, [sortedCropTemplates, isFiltering, filterCropId, activeFilters, classifyTemplateForCrop])

  const handleTuneClick = (e) => setAnchorEl(prev => prev ? null : e.currentTarget)
  const handlePopperClickAway = () => setAnchorEl(null)

  return (
    <>
      <Grid
        container
        spacing="1rem"
        rowSpacing='0.5rem'
        sx={{
          pt: '1rem',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
        }}
      >
        <Grid
          size={{ xs: 12, sm: 6, md: 5, xl: 4 }}
          sx={{
            display: 'grid',
            gridTemplateColumns: isCustomCrop ? '1fr' : `${filterIconWidth} 1fr`,
            columnGap: '1rem',
          }}
        >
          {!isCustomCrop && (
            <Box sx={{ position: 'relative' }}>
              <Badge
                variant='dot'
                overlap='circular'
                badgeContent=' '
                color='primary'
                invisible={!isFiltering}
              >
                <Button
                  variant='outlined'
                  onClick={handleTuneClick}
                  sx={{
                    borderColor: 'divider',
                    borderRadius: '8px',
                    minWidth: 0,
                    width: filterIconWidth,
                    maxWidth: filterIconWidth,
                    height: filterIconWidth,
                    minHeight: 0,
                    pr: '1rem',
                    boxSizing: 'border-box',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    justifyItems: 'center',
                  }}
                >
                  <Tune fontSize="medium" />
                </Button>
              </Badge>

              <CropFilterPopper
                anchorEl={anchorEl}
                crops={crops || []}
                filterCropId={filterCropId}
                setFilterCropId={setFilterCropId}
                activeFilters={activeFilters}
                setActiveFilters={setActiveFilters}
                onClickAway={handlePopperClickAway}
              />
            </Box>
          )}

          {isCustomCrop ? (
            <TextField
              size={isDesktop ? 'medium' : 'small'}
              autoFocus
              fullWidth
              label="Species Name"
              required
              value={customSpeciesName}
              onChange={(e) => handlers.handleCustomSpeciesNameChange(e.target.value)}
              inputProps={{ maxLength: 100 }}
              error={!customSpeciesName.trim() && speciesTouched}
            />
          ) : (
            <CropSpeciesField
              cropArchetypes={cropArchetypes}
              sortedCropTemplates={filteredTemplates}
              filterSpeciesOptions={filterSpeciesOptions}
              collectionTypes={collectionTypes}
              onSpeciesChange={handleSpeciesChange}
              onCustomCropToggle={handlers.handleCustomCropToggle}
              frostDates={frostDates}
              farmClimate={farmClimate}
            />
          )}
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 5, xl: 6 }}>
          <CropVarietyField
            onVarietyChange={handleVarietyChange}
            handlers={handlers}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 2 }}>
          <CropCountField onCountChange={handleCountChange} />
        </Grid>
      </Grid>

      {isCustomCrop && (
        <CropCustomSpeciesRow
          cropFamilyTypes={cropFamilyTypes}
          cropArchetypes={cropArchetypes}
          filterFamilyOptions={filterFamilyOptions}
          handlers={handlers}
        />
      )}
    </>
  )
}
